import { useRef, useEffect, useState, useCallback } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { Button } from "@/components/ui/button";

import { ArrowRight, TrendingUp, MessageCircle } from "lucide-react";
import { trackWhatsAppClick } from "@/lib/trackWhatsAppClick";
import salesProfessional from "@/assets/sales-professional.jpg";

const WHATSAPP_LINK = "https://api.whatsapp.com/send?phone=5548988653547&text=Ol%C3%A1%2C%20quero%20saber%20mais%20sobre%20a%20NeoSDR";

const stats = [
  { value: 300, suffix: "%", label: "Aumento médio em reuniões" },
  { value: 45, suffix: "", label: "Dias para primeiros resultados" },
  { value: 98, suffix: "%", label: "Taxa de satisfação" },
];

const stagger = 0.15;

/* ─── Animated Counter ─── */
const AnimatedCounter = ({ target, suffix }: { target: number; suffix: string }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState(0);

  const animate = useCallback(() => {
    const duration = 1400;
    let start: number | null = null;
    const step = (ts: number) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      setDisplay(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target]);

  useEffect(() => {
    if (isInView) animate();
  }, [isInView, animate]);

  return (
    <span ref={ref}>
      {display}
      {suffix}
    </span>
  );
};

/* ─── Hero ─── */
export const Hero = () => {

  const sectionRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const blobY1 = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const blobY2 = useTransform(scrollYProgress, [0, 1], [0, -50]);


  /* stagger helper */
  const item = (i: number) => ({
    initial: { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, delay: i * stagger, ease: "easeOut" as const },
  });

  return (
    <section
      ref={sectionRef}
      id="home"
      className="relative min-h-screen flex items-center bg-gradient-hero overflow-hidden pt-20"
    >
      {/* Grid bg */}
      <div className="absolute inset-0 bg-grid opacity-10" />

      {/* Animated blobs – parallax + breathing */}
      <motion.div
        style={{ y: blobY1 }}
        animate={{ scale: [1, 1.15, 1], x: [0, 12, 0] }}
        transition={{ duration: 8, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
        className="absolute top-1/4 right-0 w-96 h-96 bg-secondary/20 rounded-full blur-2xl"
      />
      <motion.div
        style={{ y: blobY2 }}
        animate={{ scale: [1, 1.1, 1], x: [0, -10, 0] }}
        transition={{ duration: 10, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
        className="absolute bottom-1/4 left-0 w-96 h-96 bg-accent/10 rounded-full blur-2xl"
      />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* ── Left: Content with stagger ── */}
          <div className="relative z-20">
            {/* Badge */}
            <motion.div {...item(0)}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/20 text-accent text-sm font-medium mb-6">
                <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                SDR as a Service Premium
              </div>
            </motion.div>

            {/* Title */}
            <motion.h1
              {...item(1)}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground leading-tight mb-6"
            >
              Geramos Reuniões Qualificadas
              <br />
              <span className="text-accent">Para Seu Comercial Vender Mais.</span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              {...item(2)}
              className="text-lg md:text-xl text-primary-foreground/80 max-w-xl mb-8 leading-relaxed"
            >
              Estruturamos e operamos a pré-venda da sua empresa para gerar reuniões qualificadas, 
              permitindo que seu time comercial foque em{" "}
              <strong className="text-primary-foreground">fechar negócios</strong>.
            </motion.p>

            {/* CTAs */}
            <motion.div {...item(3)} className="flex flex-col sm:flex-row gap-4 mb-4">
              <Button variant="hero-outline" size="xl" className="group" asChild>
                <a href="#diagnostico">
                  Quero Gerar Reuniões Agora
                  <ArrowRight className="group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
              <Button variant="whatsapp" size="xl" asChild>
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsAppClick('hero')}>
                  <MessageCircle className="w-5 h-5" />
                  Falar no WhatsApp
                </a>
              </Button>
            </motion.div>

            {/* Microcopy */}
            <motion.p {...item(3.5)} className="text-sm text-primary-foreground/60 mb-12 ml-1">
              Vagas limitadas
            </motion.p>

            {/* Stats with animated counters */}
            <div className="grid grid-cols-3 gap-6">
              {stats.map((stat, i) => (
                <motion.div key={stat.label} {...item(4 + i)}>
                  <div className="text-2xl md:text-3xl font-bold text-accent mb-1">
                    <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                  </div>
                  <div className="text-sm text-primary-foreground/60">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* ── Right: Visual (image + video) ── */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
            className="hidden lg:block"
          >
            <div className="relative">
              {/* Blob shape behind */}
              <div className="absolute -inset-6 bg-gradient-to-br from-secondary/30 to-accent/20 rounded-[40%_60%_70%_30%/40%_50%_60%_50%] blur-sm" />

              {/* Professional image – blob shape */}
              <div className="relative rounded-[30%_70%_60%_40%/50%_40%_60%_50%] overflow-hidden shadow-2xl">
                <img
                  src={salesProfessional}
                  alt="Profissional de vendas usando NeoSDR"
                  className="w-full h-[480px] object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/40 via-transparent to-transparent" />
              </div>

              {/* Floating Metric Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1 }}
                className="absolute -right-4 bottom-12 bg-card rounded-xl p-4 shadow-lg border border-border z-10"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                    <TrendingUp className="text-secondary" size={18} />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-foreground">156</div>
                    <div className="text-xs text-muted-foreground">reuniões/mês</div>
                  </div>
                  <span className="text-xs font-semibold text-secondary bg-secondary/10 px-2 py-0.5 rounded-full">
                    +18%
                  </span>
                </div>
              </motion.div>

              {/* Decorative floating blobs */}
              <motion.div
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-8 -left-8 w-24 h-24 bg-accent/20 rounded-full blur-xl"
              />
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="absolute -bottom-6 -right-10 w-32 h-32 bg-secondary/15 rounded-full blur-xl"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
